import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Menu } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import styles from './WorkspaceNavigation.module.scss';

type WorkspaceNavigationProps = {
  children: React.ReactNode;
  searchInputRef?: React.Ref<any>;
};

type FilterResult = {
  nodes: React.ReactNode[];
  itemCount: number;
  openKeys: string[];
};

// Each route mounts its own LayoutWrapper (see App.tsx), so navigating
// remounts this component and the menu would scroll back to the top. Keeping
// the offset outside the component preserves the reader's place; a full page
// load starts from the top again, which is what you want.
let lastScrollTop = 0;

const nodeText = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (!React.isValidElement(node)) {
    return '';
  }

  return nodeText(node.props.children);
};

const filterNodes = (
  nodes: React.ReactNode,
  query: string,
  showAllChildren = false
): FilterResult => {
  const result: FilterResult = { nodes: [], itemCount: 0, openKeys: [] };

  React.Children.forEach(nodes, (node) => {
    if (!React.isValidElement(node)) {
      return;
    }

    if (node.type === Menu.Item) {
      if (
        showAllChildren ||
        nodeText(node.props.children).toLocaleLowerCase().includes(query)
      ) {
        result.nodes.push(node);
        result.itemCount += 1;
      }
      return;
    }

    const titleMatches = nodeText(node.props.title)
      .toLocaleLowerCase()
      .includes(query);
    const childResult = filterNodes(
      node.props.children,
      query,
      showAllChildren || titleMatches
    );

    if (childResult.itemCount > 0) {
      result.nodes.push(React.cloneElement(node, undefined, childResult.nodes));
      result.itemCount += childResult.itemCount;
      result.openKeys.push(...childResult.openKeys);

      if (node.type === Menu.SubMenu && node.key != null) {
        result.openKeys.push(String(node.key));
      }
    }
  });

  return result;
};

export const WorkspaceNavigation: React.FC<WorkspaceNavigationProps> = ({
  children,
  searchInputRef,
}) => {
  const menus = React.Children.toArray(children).filter(
    React.isValidElement
  ) as React.ReactElement<any>[];
  const [query, setQuery] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const defaultOpenKeys = menus.reduce<string[]>(
    (keys, menu) => [...keys, ...(menu.props.defaultOpenKeys || [])],
    []
  );
  const defaultOpenKeysSignature = defaultOpenKeys.join('|');
  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys);
  const normalizedQuery = query.trim().toLocaleLowerCase();

  useEffect(() => {
    setOpenKeys((currentKeys) =>
      Array.from(new Set([...currentKeys, ...defaultOpenKeys]))
    );
    // The signature changes only when the active route requires new ancestors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpenKeysSignature]);

  useEffect(() => {
    if (scrollerRef.current && lastScrollTop) {
      scrollerRef.current.scrollTop = lastScrollTop;
    }
  }, []);

  const filtered = useMemo(() => {
    if (!normalizedQuery) {
      return undefined;
    }

    return menus.reduce<FilterResult & { menuChildren: React.ReactNode[][] }>(
      (result, menu) => {
        const menuResult = filterNodes(menu.props.children, normalizedQuery);
        return {
          nodes: [],
          itemCount: result.itemCount + menuResult.itemCount,
          openKeys: [...result.openKeys, ...menuResult.openKeys],
          menuChildren: [...result.menuChildren, menuResult.nodes],
        };
      },
      { nodes: [], itemCount: 0, openKeys: [], menuChildren: [] }
    );
  }, [menus, normalizedQuery]);
  const visibleOpenKeys = filtered ? filtered.openKeys : openKeys;

  const navigationMenus = menus.map((menu, index) =>
    React.cloneElement(
      menu,
      {
        key: menu.key || `workspace-menu-${index}`,
        defaultOpenKeys: undefined,
        openKeys: visibleOpenKeys,
        onOpenChange: (keys: string[]) => {
          if (!normalizedQuery) {
            setOpenKeys(keys);
            menu.props.onOpenChange?.(keys);
          }
        },
      },
      filtered ? filtered.menuChildren[index] : menu.props.children
    )
  );

  return (
    <nav aria-label="Primary navigation" className={styles.root}>
      <div className={styles.searchWrapper}>
        <div className={styles.search}>
          <SearchOutlined aria-hidden="true" />
          <input
            ref={searchInputRef as React.Ref<HTMLInputElement>}
            // A text input with the searchbox role: type="search" adds a second
            // native clear button next to the one below, and its selection API
            // is unimplemented in jsdom, which reverses typed text under test.
            type="text"
            role="searchbox"
            aria-label="Search navigation"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search navigation"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear navigation search"
              onClick={() => setQuery('')}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div
        ref={scrollerRef}
        className={styles.scroller}
        onScroll={(event) => {
          lastScrollTop = event.currentTarget.scrollTop;
        }}
      >
        {filtered && filtered.itemCount === 0 ? (
          <p className={styles.empty} role="status">
            No navigation results
          </p>
        ) : (
          navigationMenus
        )}
      </div>
    </nav>
  );
};
